import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight, Phone, Car, Building2, Hash, TrendingUp, Megaphone, Loader2, CheckCircle, Send, Instagram, Twitter, MessageCircle, Facebook, Hash as Discord, Users, Store, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Breadcrumb from '../components/Breadcrumb';
import DashboardLayout from '../components/DashboardLayout';
import CompanyMarketingHub from './marketing/CompanyMarketingHub';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Marketing() {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { token } = useAuth();
  
  // Platform selection state
  const [selectedPlatform, setSelectedPlatform] = useState(searchParams.get('platform') || null);
  
  const [items, setItems] = useState([]);
  const [publishedItems, setPublishedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marketingTab, setMarketingTab] = useState('not_published'); // 'not_published' or 'published'
  
  // Marketing detail page state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showMarketingPage, setShowMarketingPage] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [publishing, setPublishing] = useState(false);

  // iter-286 (Feb 2026) — Social channels split into "active" (Meta API
  // covers Instagram / Facebook / WhatsApp) and "coming soon" (X /
  // Snapchat / Discord / Pinterest / Telegram — not yet wired to the
  // Meta app). Disabled ones render greyscale + can't be selected.
  const socialChannels = [
    { id: 'instagram', name: 'انستغرام',        nameEn: 'Instagram',         icon: '📸', color: 'bg-gradient-to-r from-purple-500 to-pink-500', disabled: false },
    { id: 'facebook',  name: 'فيسبوك',           nameEn: 'Facebook',          icon: '📘', color: 'bg-blue-600',                                     disabled: false },
    { id: 'whatsapp',  name: 'قناة الواتساب',    nameEn: 'WhatsApp Channel',  icon: '💬', color: 'bg-green-500',                                    disabled: false },
    { id: 'x',         name: 'اكس',              nameEn: 'X (Twitter)',       icon: '𝕏',  color: 'bg-black',                                       disabled: true  },
    { id: 'snapchat',  name: 'سناب شات',         nameEn: 'Snapchat',          icon: '👻', color: 'bg-yellow-400',                                  disabled: true  },
    { id: 'telegram',  name: 'تلغرام',           nameEn: 'Telegram',          icon: '✈️', color: 'bg-blue-500',                                    disabled: true  },
    { id: 'discord',   name: 'ديسكورد',          nameEn: 'Discord',           icon: '🎮', color: 'bg-indigo-600',                                  disabled: true  },
    { id: 'pinterest', name: 'بينترست',          nameEn: 'Pinterest',         icon: '📌', color: 'bg-red-600',                                     disabled: true  },
  ];

  const getHeaders = () => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  const marketingCategories = [
    {
      id: 'phone-numbers',
      nameAr: 'تسويق أرقام الموبايل',
      nameEn: 'Mobile Numbers Marketing',
      icon: <Phone className="h-10 w-10 text-blue-500" />,
      color: 'blue',
      storeType: 'phone_numbers'
    },
    {
      id: 'car-plates',
      nameAr: 'تسويق لوحات الأرقام',
      nameEn: 'Car Plates Marketing',
      icon: <Hash className="h-10 w-10 text-amber-500" />,
      color: 'amber',
      storeType: 'car_plates'
    },
    {
      id: 'real-estate',
      nameAr: 'تسويق العقارات',
      nameEn: 'Real Estate Marketing',
      icon: <Building2 className="h-10 w-10 text-emerald-500" />,
      color: 'emerald',
      storeType: 'real_estate'
    },
    {
      id: 'cars',
      nameAr: 'تسويق السيارات',
      nameEn: 'Cars Marketing',
      icon: <Car className="h-10 w-10 text-purple-500" />,
      color: 'purple',
      storeType: 'cars'
    },
  ];

  const currentCategory = marketingCategories.find(c => c.id === categoryId);

  // جلب العناصر
  const fetchItems = async () => {
    if (!currentCategory) return;
    setLoading(true);
    try {
      const headers = getHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/admin/marketing/items?store_type=${currentCategory.storeType}`, { headers });
      const allItems = res.data.data || [];
      
      // فصل العناصر المنشورة عن غير المنشورة
      setItems(allItems.filter(item => !item.is_published));
      setPublishedItems(allItems.filter(item => item.is_published));
    } catch (err) {
      console.log('Error fetching items:', err.message);
      setItems([]);
      setPublishedItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (categoryId) {
      fetchItems();
    }
  }, [categoryId]);

  // تبديل اختيار قناة
  const toggleChannel = (channelId) => {
    // iter-286 — Block toggle for disabled channels (X / Snapchat /
    // Telegram / Discord / Pinterest are not yet wired).
    const channel = socialChannels.find(c => c.id === channelId);
    if (channel?.disabled) {
      toast.info(language === 'ar' ? 'قريباً — لم يتم ربط هذه القناة بعد' : 'Coming soon — this channel is not connected yet');
      return;
    }
    setSelectedChannels(prev => 
      prev.includes(channelId) 
        ? prev.filter(id => id !== channelId)
        : [...prev, channelId]
    );
  };

  // نشر على القنوات المحددة
  const publishToChannels = async () => {
    if (selectedChannels.length === 0) {
      toast.error(language === 'ar' ? 'يرجى اختيار قناة واحدة على الأقل' : 'Please select at least one channel');
      return;
    }
    
    setPublishing(true);
    try {
      const headers = getHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/marketing/publish`, {
        item_id: selectedItem.id,
        channels: selectedChannels,
        item_type: categoryId,
      }, { headers });
      
      toast.success(language === 'ar' ? 'تم إرسال للنشر بنجاح!' : 'Successfully sent for publishing!');
      setShowMarketingPage(false);
      setSelectedItem(null);
      setSelectedChannels([]);
      fetchItems(); // تحديث القائمة
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في النشر' : 'Failed to publish'));
    } finally {
      setPublishing(false);
    }
  };

  // عرض صفحة اختيار المنصة (CLOUDGM)
  if (!categoryId && !selectedPlatform) {
    return (
      <DashboardLayout
        title={language === 'ar' ? 'CLOUDGM FZ LLC (تسويق)' : 'CLOUDGM FZ LLC (Marketing)'}
        titleEn="CLOUDGM FZ LLC (Marketing)"
        subtitle={language === 'ar' ? 'اختر المنصة لإدارة التسويق' : 'Select platform to manage marketing'}
        subtitleEn="Select platform to manage marketing"
        screenCode="MKT-001"
        backPath="/dashboard"
        breadcrumb={[
          { labelAr: 'التسويق', labelEn: 'Marketing', path: '/dashboard?view=marketing' },
          { labelAr: 'CLOUDGM FZ LLC (تسويق)', labelEn: 'CLOUDGM FZ LLC (Marketing)', path: null }
        ]}
      >
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-xl font-bold text-white mb-8 text-center flex items-center justify-center gap-3">
            <Megaphone className="h-6 w-6 text-[#d4af37]" />
            {language === 'ar' ? 'CLOUDGM - اختر المنصة' : 'CLOUDGM - Select Platform'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* منصة العملاء */}
            <div
              onClick={() => setSelectedPlatform('customers')}
              className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 hover:border-[#1E9BD9]/50 transition-all duration-300 cursor-pointer hover:-translate-y-2 p-8"
              data-testid="marketing-customers-platform"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-xl bg-[#1E9BD9]/10 border border-[#1E9BD9]/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Users className="h-10 w-10 text-[#1E9BD9]" />
              </div>
              <h3 className="text-xl font-bold text-white text-center mb-2">
                {language === 'ar' ? 'منصة العملاء' : 'Customers Platform'}
              </h3>
              <p className="text-sm text-[#71717A] text-center">CLOUDGM FZ LLC</p>
            </div>

            {/* منصة التجار */}
            <div
              onClick={() => toast.info(language === 'ar' ? 'قريباً' : 'Coming Soon')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 hover:border-[#8B5CF6]/50 transition-all duration-300 cursor-pointer hover:-translate-y-2 p-8"
              data-testid="marketing-merchants-platform"
            >
              <div className="absolute top-3 right-3 px-2 py-0.5 bg-amber-500/10 rounded-md">
                <span className="text-[10px] text-amber-400">{language === 'ar' ? 'قريباً' : 'Soon'}</span>
              </div>
              <div className="w-20 h-20 mx-auto mb-6 rounded-xl bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Store className="h-10 w-10 text-[#8B5CF6]" />
              </div>
              <h3 className="text-xl font-bold text-white text-center mb-2">
                {language === 'ar' ? 'منصة التجار' : 'Merchants Platform'}
              </h3>
              <p className="text-sm text-[#71717A] text-center">CLOUDGM BUSINESS</p>
            </div>

            {/* تسويق Cloud GM (Private / Company-only) */}
            <div
              onClick={() => setSelectedPlatform('company')}
              className="group relative bg-gradient-to-br from-[#d4af37]/[0.06] to-white/[0.02] backdrop-blur-xl rounded-2xl overflow-hidden border border-[#d4af37]/30 hover:border-[#d4af37]/70 transition-all duration-300 cursor-pointer hover:-translate-y-2 p-8"
              data-testid="marketing-company-platform"
            >
              <div className="absolute top-3 right-3 px-2 py-0.5 bg-[#d4af37]/10 rounded-md">
                <span className="text-[10px] text-[#d4af37] font-semibold">
                  {language === 'ar' ? 'خاص بالشركة' : 'Company-only'}
                </span>
              </div>
              <div className="w-20 h-20 mx-auto mb-6 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Megaphone className="h-10 w-10 text-[#d4af37]" />
              </div>
              <h3 className="text-xl font-bold text-white text-center mb-2">
                {language === 'ar' ? 'تسويق Cloud GM' : 'Cloud GM Marketing'}
              </h3>
              <p className="text-sm text-[#71717A] text-center">
                {language === 'ar' ? 'الحملات والمواد الترويجية للشركة' : 'Company campaigns & assets'}
              </p>
            </div>

            {/* Meta App Review Testing */}
            <div
              onClick={() => navigate('/marketing/meta-app-review')}
              className="group relative bg-gradient-to-br from-emerald-500/[0.06] to-white/[0.02] backdrop-blur-xl rounded-2xl overflow-hidden border border-emerald-500/30 hover:border-emerald-500/70 transition-all duration-300 cursor-pointer hover:-translate-y-2 p-8"
              data-testid="marketing-meta-review-card"
            >
              <div className="absolute top-3 right-3 px-2 py-0.5 bg-emerald-500/10 rounded-md">
                <span className="text-[10px] text-emerald-300 font-semibold">
                  {language === 'ar' ? 'Meta' : 'Meta'}
                </span>
              </div>
              <div className="w-20 h-20 mx-auto mb-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-10 w-10 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white text-center mb-2">
                {language === 'ar' ? 'اختبارات Meta App Review' : 'Meta App Review Testing'}
              </h3>
              <p className="text-sm text-[#71717A] text-center">
                {language === 'ar'
                  ? '11 قدرة · WhatsApp · Instagram · Facebook · Marketing API'
                  : '11 capabilities · WhatsApp · Instagram · Facebook · Marketing API'}
              </p>
            </div>
          </div>
        </section>
      </DashboardLayout>
    );
  }

  // عرض تسويق Cloud GM (Private / Company-only)
  if (!categoryId && selectedPlatform === 'company') {
    return (
      <DashboardLayout
        title={language === 'ar' ? 'تسويق Cloud GM' : 'Cloud GM Marketing'}
        titleEn="Cloud GM Marketing"
        subtitle={language === 'ar' ? 'مكتبة المواد التسويقية الخاصة بالشركة (غير مرئية للعملاء أو التجار)' : 'Private company marketing library (not visible to customers or merchants)'}
        subtitleEn="Private company marketing library"
        screenCode="MKT-COMPANY"
        onBack={() => setSelectedPlatform(null)}
        breadcrumb={[
          { labelAr: 'التسويق', labelEn: 'Marketing', path: '/dashboard?view=marketing' },
          { labelAr: 'CLOUDGM FZ LLC (تسويق)', labelEn: 'CLOUDGM FZ LLC (Marketing)', path: '/marketing' },
          { labelAr: 'تسويق Cloud GM', labelEn: 'Cloud GM Marketing', path: null }
        ]}
      >
        <CompanyMarketingHub language={language} onBack={() => setSelectedPlatform(null)} />
      </DashboardLayout>
    );
  }

  // عرض صفحة اختيار نوع التسويق (منصة العملاء)
  if (!categoryId && selectedPlatform === 'customers') {
    return (
      <DashboardLayout
        title={language === 'ar' ? 'منصة العملاء (تسويق)' : 'Customers Platform (Marketing)'}
        titleEn="Customers Platform (Marketing)"
        subtitle={language === 'ar' ? 'اختر نوع التسويق' : 'Select marketing type'}
        subtitleEn="Select marketing type"
        screenCode="MKT-002"
        onBack={() => setSelectedPlatform(null)}
        breadcrumb={[
          { labelAr: 'التسويق', labelEn: 'Marketing', path: '/dashboard?view=marketing' },
          { labelAr: 'CLOUDGM FZ LLC (تسويق)', labelEn: 'CLOUDGM FZ LLC (Marketing)', path: '/marketing' },
          { labelAr: 'منصة العملاء (تسويق)', labelEn: 'Customers Platform (Marketing)', path: null }
        ]}
      >
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-xl font-bold text-white mb-8 text-center flex items-center justify-center gap-3">
            <Megaphone className="h-6 w-6 text-[#d4af37]" />
            {language === 'ar' ? 'اختر نوع التسويق' : 'Select Marketing Type'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {marketingCategories.map((category) => (
              <div
                key={category.id}
                onClick={() => navigate(`/marketing/${category.id}?platform=customers`)}
                className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 hover:border-[#d4af37]/50 transition-all duration-300 cursor-pointer hover:-translate-y-2"
                data-testid={`marketing-${category.id}`}
              >
                <div className="h-32 flex items-center justify-center">
                  <div className="group-hover:scale-110 transition-transform duration-300">
                    {category.icon}
                  </div>
                </div>

                <div className="p-5 text-center border-t border-white/10 transition-colors">
                  <h3 className="text-base font-bold text-white">
                    {language === 'ar' ? category.nameAr : category.nameEn}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </section>
      </DashboardLayout>
    );
  }

  // عرض صفحة اختيار نوع التسويق (القديمة - fallback)
  if (!categoryId) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="mb-4">
            <Breadcrumb pathAr="لوحة التحكم / التسويق" pathEn="Dashboard / Marketing" />
          </div>
          
          {/* Header */}
          <div className="flex items-center justify-between mb-8 animate-header">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#c9a227]/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-[#c9a227]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {language === 'ar' ? 'التسويق' : 'Marketing'}
                </h1>
                <p className="text-sm text-[#808080]">
                  {language === 'ar' ? 'إدارة حملات التسويق' : 'Marketing Campaigns Management'}
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl btn-animate"
              data-testid="back-btn"
            >
              <ArrowRight className="h-4 w-4 me-2" />
              {language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
          </div>

          {/* Marketing Categories */}
          <div className="animate-fade-in">
            <h2 className="text-2xl font-bold text-white mb-8 text-center flex items-center justify-center gap-3">
              <Megaphone className="h-7 w-7 text-[#c9a227]" />
              {language === 'ar' ? 'اختر نوع التسويق' : 'Select Marketing Type'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-stagger">
              {marketingCategories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => navigate(`/marketing/${category.id}`)}
                  className="group bg-[#1a1a2e] rounded-2xl overflow-hidden border border-[#2a2a4e] hover:border-[#c9a227] transition-all duration-300 cursor-pointer hover:-translate-y-2"
                  data-testid={`marketing-${category.id}`}
                >
                  <div className="h-32 flex items-center justify-center bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a]">
                    <div className="group-hover:scale-110 transition-transform duration-300">
                      {category.icon}
                    </div>
                  </div>

                  <div className="p-5 text-center border-t border-[#2a2a4e] transition-colors">
                    <h3 className="text-lg font-bold text-white mb-2">
                      {language === 'ar' ? category.nameAr : category.nameEn}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // iter-286 (Feb 2026) — Marketing publish flow now renders as a MODAL
  // (card overlay) instead of a full page. The user experience is
  // "click 'تسويق' on a row → card slides in → pick channels → publish".
  // The card is defined below the main return and shown when
  // showMarketingPage && selectedItem are both truthy.
  const closeMarketingModal = () => {
    setShowMarketingPage(false);
    setSelectedItem(null);
    setSelectedChannels([]);
  };

  const MarketingModal = () => {
    if (!showMarketingPage || !selectedItem) return null;
    return (
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={closeMarketingModal}
        data-testid="marketing-modal-backdrop"
      >
        <div
          className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0f0f1e] border border-[#c9a227]/30 rounded-2xl shadow-[0_0_60px_rgba(201,162,39,0.15)] animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
          data-testid="marketing-modal"
        >
          {/* Header with brand strip */}
          <div className="sticky top-0 z-10 bg-[#0f0f1e]/95 backdrop-blur border-b border-[#c9a227]/20 rounded-t-2xl">
            <div className="p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-[#c9a227]/15 rounded-xl flex items-center justify-center">
                  <Send className="h-5 w-5 text-[#c9a227]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">
                    {language === 'ar' ? 'نشر وتسويق' : 'Publish & Market'}
                  </h2>
                  <p className="text-xs text-[#808080]">
                    {currentCategory?.[language === 'ar' ? 'nameAr' : 'nameEn']}
                  </p>
                </div>
              </div>
              <button
                onClick={closeMarketingModal}
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white flex items-center justify-center transition"
                data-testid="marketing-modal-close"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="p-5 space-y-6">
            {/* Item Details Card */}
            <div className="bg-[#1a1a2e] rounded-xl p-4 border border-[#2a2a4e]">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                {currentCategory?.icon && <span className="scale-75">{currentCategory.icon}</span>}
                {language === 'ar' ? 'تفاصيل المعروض' : 'Item Details'}
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-[#0a0a0a] rounded-lg p-3 border border-[#2a2a4e]">
                  <p className="text-[10px] text-[#808080] mb-1">{language === 'ar' ? 'الرقم/العنوان' : 'Number/Title'}</p>
                  <p className="font-bold text-white text-base" dir="ltr">
                    {selectedItem.number || selectedItem.title || selectedItem.name || '-'}
                  </p>
                </div>
                <div className="bg-[#0a0a0a] rounded-lg p-3 border border-[#2a2a4e]">
                  <p className="text-[10px] text-[#808080] mb-1">{language === 'ar' ? 'السعر' : 'Price'}</p>
                  <p className="font-bold text-[#c9a227] text-base">
                    {selectedItem.price ? `${selectedItem.price} ${language === 'ar' ? 'ر.س' : 'SAR'}` : '-'}
                  </p>
                </div>
                {selectedItem.description && (
                  <div className="bg-[#0a0a0a] rounded-lg p-3 border border-[#2a2a4e] col-span-2">
                    <p className="text-[10px] text-[#808080] mb-1">{language === 'ar' ? 'الوصف' : 'Description'}</p>
                    <p className="text-white text-sm">{selectedItem.description}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Channels Grid */}
            <div className="bg-[#1a1a2e] rounded-xl p-4 border border-[#2a2a4e]">
              <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-[#c9a227]" />
                {language === 'ar' ? 'اختر قنوات النشر' : 'Select Publishing Channels'}
              </h3>
              <p className="text-xs text-[#808080] mb-4">
                {language === 'ar'
                  ? 'المفعّل: انستغرام + فيسبوك + قناة الواتساب (عبر Meta API). الباقي قريباً.'
                  : 'Active: Instagram + Facebook + WhatsApp Channel (via Meta API). Rest coming soon.'}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {socialChannels.map((channel) => {
                  const isSelected = selectedChannels.includes(channel.id);
                  const isDisabled = channel.disabled;
                  return (
                    <div
                      key={channel.id}
                      onClick={() => toggleChannel(channel.id)}
                      className={`relative p-3 rounded-xl border-2 transition-all duration-200 ${
                        isDisabled
                          ? 'border-white/5 bg-[#0a0a0a]/60 cursor-not-allowed opacity-50'
                          : isSelected
                            ? 'border-[#c9a227] bg-[#c9a227]/10 cursor-pointer'
                            : 'border-[#2a2a4e] bg-[#0a0a0a] hover:border-[#3a3a5e] cursor-pointer'
                      }`}
                      data-testid={`modal-channel-${channel.id}`}
                    >
                      {isSelected && !isDisabled && (
                        <div className="absolute top-1 left-1">
                          <CheckCircle className="h-4 w-4 text-[#c9a227]" />
                        </div>
                      )}
                      {isDisabled && (
                        <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-white/10 text-[9px] text-white/60 font-medium">
                          {language === 'ar' ? 'قريباً' : 'soon'}
                        </div>
                      )}
                      <div className="text-center">
                        <div className={`w-10 h-10 ${channel.color} rounded-lg flex items-center justify-center mx-auto mb-1.5 text-white text-lg ${isDisabled ? 'grayscale' : ''}`}>
                          {channel.icon}
                        </div>
                        <p className={`text-xs font-medium ${isDisabled ? 'text-white/50' : 'text-white'}`}>
                          {language === 'ar' ? channel.name : channel.nameEn}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2 border-t border-white/5">
              <Button
                variant="outline"
                onClick={closeMarketingModal}
                className="border-[#2a2a4e] text-white hover:bg-[#1a1a2e]"
                data-testid="marketing-modal-cancel"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={publishToChannels}
                disabled={publishing || selectedChannels.length === 0}
                className="bg-[#c9a227] hover:bg-[#b8922a] text-black min-w-[180px]"
                data-testid="marketing-modal-publish"
              >
                {publishing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 me-2" />
                    {language === 'ar'
                      ? `نشر على ${selectedChannels.length} قنوات`
                      : `Publish to ${selectedChannels.length} channels`}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // عرض قائمة العناصر للتسويق
  const getCategoryBreadcrumb = () => {
    return [
      { labelAr: 'التسويق', labelEn: 'Marketing', path: '/dashboard?view=marketing' },
      { labelAr: 'CLOUDGM FZ LLC (تسويق)', labelEn: 'CLOUDGM FZ LLC (Marketing)', path: '/marketing' },
      { labelAr: 'منصة العملاء (تسويق)', labelEn: 'Customers Platform (Marketing)', path: '/marketing?platform=customers' },
      { labelAr: currentCategory?.nameAr || '', labelEn: currentCategory?.nameEn || '', path: null }
    ];
  };

  return (
    <DashboardLayout
      title={language === 'ar' ? currentCategory?.nameAr : currentCategory?.nameEn}
      titleEn={currentCategory?.nameEn || 'Marketing'}
      subtitle={language === 'ar' ? 'إدارة حملات التسويق' : 'Marketing Campaigns Management'}
      subtitleEn="Marketing Campaigns Management"
      screenCode="MKT-003"
      onBack={() => navigate('/marketing?platform=customers')}
      breadcrumb={getCategoryBreadcrumb()}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Switch Tabs */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 bg-white/[0.02] p-1 rounded-xl border border-white/10">
            <button
              onClick={() => setMarketingTab('not_published')}
              className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                marketingTab === 'not_published'
                  ? 'bg-amber-500 text-black'
                  : 'text-[#71717A] hover:text-white'
              }`}
              data-testid="tab-not-published"
            >
              {language === 'ar' ? 'لم يتم التسويق' : 'Not Published'}
              {items.length > 0 && (
                <span className={`ms-2 px-2 py-0.5 rounded-full text-xs ${
                  marketingTab === 'not_published' ? 'bg-black/20' : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {items.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setMarketingTab('published')}
              className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                marketingTab === 'published'
                  ? 'bg-emerald-500 text-black'
                  : 'text-[#71717A] hover:text-white'
              }`}
              data-testid="tab-published"
            >
              {language === 'ar' ? 'تم النشر والتسويق' : 'Published'}
              {publishedItems.length > 0 && (
                <span className={`ms-2 px-2 py-0.5 rounded-full text-xs ${
                  marketingTab === 'published' ? 'bg-black/20' : 'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {publishedItems.length}
                </span>
              )}
            </button>
          </div>

          <Button
            onClick={fetchItems}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5"
          >
            {language === 'ar' ? 'تحديث' : 'Refresh'}
          </Button>
        </div>

        {/* Items */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-white/[0.02] rounded-xl animate-pulse border border-white/5" />)}
          </div>
        ) : (marketingTab === 'not_published' ? items : publishedItems).length === 0 ? (
          <div className="text-center py-16 text-[#71717A] bg-white/[0.02] rounded-2xl border border-white/10">
            {currentCategory?.icon && (
              <div className="mx-auto mb-4 opacity-30 scale-150">
                {currentCategory.icon}
              </div>
            )}
            <p className="text-lg">
              {marketingTab === 'not_published' 
                ? (language === 'ar' ? 'لا توجد عناصر للتسويق' : 'No items to market')
                : (language === 'ar' ? 'لا توجد عناصر منشورة' : 'No published items')
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {(marketingTab === 'not_published' ? items : publishedItems).map((item, idx) => (
              <div key={item.id || idx} className="p-5 bg-white/[0.02] rounded-2xl border border-white/10 hover:border-[#d4af37]/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#d4af37]/20 flex items-center justify-center">
                      {currentCategory?.id === 'phone-numbers' && <Phone className="h-7 w-7 text-blue-500" />}
                      {currentCategory?.id === 'car-plates' && <Hash className="h-7 w-7 text-amber-500" />}
                      {currentCategory?.id === 'real-estate' && <Building2 className="h-7 w-7 text-emerald-500" />}
                      {currentCategory?.id === 'cars' && <Car className="h-7 w-7 text-purple-500" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-white text-lg" dir="ltr">
                          {item.number || item.title || item.name || (language === 'ar' ? 'غير محدد' : 'Unknown')}
                        </p>
                        {(item.is_merchant || item.store_id) && (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full border border-emerald-500/30">
                            {language === 'ar' ? 'تاجر' : 'Merchant'}
                          </span>
                        )}
                        {item.is_published && (
                          <span className="px-2 py-0.5 bg-[#d4af37]/20 text-[#d4af37] text-xs rounded-full border border-[#d4af37]/30">
                            {language === 'ar' ? 'تم النشر' : 'Published'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#71717A]">
                        {item.owner_name || item.user_name || (language === 'ar' ? 'مستخدم' : 'User')}
                        {item.price && <span className="mx-2">•</span>}
                        {item.price && <span className="text-[#d4af37]">{item.price} {language === 'ar' ? 'ر.س' : 'SAR'}</span>}
                      </p>
                      {item.created_at && (
                        <p className="text-xs text-[#505050] mt-1">
                          {new Date(item.created_at).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* زر التسويق فقط */}
                  {marketingTab === 'not_published' && (
                    <Button
                      onClick={() => {
                        setSelectedItem(item);
                        setShowMarketingPage(true);
                      }}
                      className="bg-[#d4af37] hover:bg-[#c9a227] text-black px-6"
                      data-testid={`market-item-${item.id || idx}`}
                    >
                      <Send className="h-4 w-4 me-2" />
                      {language === 'ar' ? 'تسويق' : 'Market'}
                    </Button>
                  )}
                  
                  {/* للعناصر المنشورة - عرض القنوات */}
                  {marketingTab === 'published' && item.published_channels && (
                    <div className="flex items-center gap-2">
                      {item.published_channels.map(channelId => {
                        const channel = socialChannels.find(c => c.id === channelId);
                        return channel ? (
                          <div key={channelId} className={`w-8 h-8 ${channel.color} rounded-lg flex items-center justify-center text-white text-sm`}>
                            {channel.icon}
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      {/* iter-286 — Publish modal (renders over content when active) */}
      <MarketingModal />
    </DashboardLayout>
  );
}
